import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  FormControlLabel,
  Checkbox,
  Switch,
  FormGroup,
  Divider,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperAdminIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import PasswordTextField from "../components/PasswordTextField";

interface Admin {
  id: string;
  username: string;
  role: string;
  isSuperAdmin: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

interface AdminFormData {
  username: string;
  password: string;
  isSuperAdmin: boolean;
  permissions: string[];
}

// All available menu items that can be assigned as permissions
const ALL_MENU_ITEMS = [
  "Home",
  "Dashboard",
  "Players",
  "League Days",
  "Tournaments",
  "Rankings",
  "Finances",
  "Financial Calculator",
  "Penalties & Bonuses",
  "Nations League",
  "Announcements",
  "Scorekeeper Requests",
  "Manage Scorekeepers",
  "Player Accounts",
  "Deletion Requests",
  "Tournament Registrations",
  "Admin Users",
  "Seasons",
  "Court Management",
  "Email Settings",
  "Season Registrations",
  "Transfers",
  "Navigation Settings",
  "Blocked IPs",
  "Dangerous",
];

const AdminManagement: React.FC = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);

  // Form state
  const [formData, setFormData] = useState<AdminFormData>({
    username: "",
    password: "",
    isSuperAdmin: false,
    permissions: [],
  });
  const [formErrors, setFormErrors] = useState<Partial<AdminFormData>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load admins on mount
  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");

      const response = await apiRequest('/api/admins', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      setAdmins(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load admins");
      console.error("Error loading admins:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (admin?: Admin) => {
    if (admin) {
      // Edit mode
      setEditMode(true);
      setSelectedAdmin(admin);
      setFormData({
        username: admin.username,
        password: "", // Don't pre-fill password
        isSuperAdmin: admin.isSuperAdmin || false,
        permissions: admin.permissions || [],
      });
    } else {
      // Create mode
      setEditMode(false);
      setSelectedAdmin(null);
      setFormData({
        username: "",
        password: "",
        isSuperAdmin: false,
        permissions: [],
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedAdmin(null);
    setFormData({ username: "", password: "", isSuperAdmin: false, permissions: [] });
    setFormErrors({});
  };

  const handleOpenDeleteDialog = (admin: Admin) => {
    setSelectedAdmin(admin);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedAdmin(null);
  };

  const validateForm = (): boolean => {
    const errors: Partial<AdminFormData> = {};

    if (!formData.username.trim()) {
      errors.username = "Username is required";
    }

    // Password is required for new admins, optional for editing own account
    if (!editMode && !formData.password) {
      errors.password = "Password is required";
    } else if (formData.password && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);
      const token = localStorage.getItem("authToken");

      if (editMode && selectedAdmin) {
        // Update admin account
        const updateData: any = {
          username: formData.username,
        };

        // Only include password if it was changed
        if (formData.password) {
          updateData.password = formData.password;
        }

        // Only super admins can update permissions and super admin status
        const isOwnAccount = user?.id === selectedAdmin.id;
        if (user?.isSuperAdmin && !isOwnAccount) {
          updateData.isSuperAdmin = formData.isSuperAdmin;
          updateData.permissions = formData.permissions;
        }

        await apiRequest(`/api/admins/${selectedAdmin.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify(updateData),
        });

        setSuccess("Account updated successfully");

        // Update localStorage if username changed and it's own account
        if (isOwnAccount && user && formData.username !== user.username) {
          const updatedUser = { ...user, username: formData.username };
          localStorage.setItem("authUser", JSON.stringify(updatedUser));
        }
      } else {
        // Create new admin
        await apiRequest('/api/admins', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify(formData),
        });

        setSuccess("Admin created successfully");
      }

      handleCloseDialog();
      loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error || "Operation failed");
      console.error("Error submitting form:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;

    try {
      setSubmitting(true);
      setError(null);
      const token = localStorage.getItem("authToken");

      await apiRequest(`/api/admins/${selectedAdmin.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess("Admin deleted successfully");
      handleCloseDeleteDialog();
      loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete admin");
      console.error("Error deleting admin:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AdminIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h4">Admin Management</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="small"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Add Admin
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No admins found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => {
                const isOwnAccount = user?.id === admin.id;
                const canEdit = isOwnAccount || (user?.isSuperAdmin === true);
                return (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {admin.username}
                        </Typography>
                        {isOwnAccount && (
                          <Chip
                            label="You"
                            size="small"
                            color="secondary"
                          />
                        )}
                        {admin.isSuperAdmin && (
                          <Chip
                            icon={<SuperAdminIcon />}
                            label="Super Admin"
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={admin.role} color="primary" size="small" />
                    </TableCell>
                    <TableCell>{formatDate(admin.createdAt)}</TableCell>
                    <TableCell>{formatDate(admin.updatedAt)}</TableCell>
                    <TableCell align="right">
                      {canEdit ? (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(admin)}
                            title={isOwnAccount ? "Edit my account" : "Edit admin"}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(admin)}
                            title={isOwnAccount ? "Delete my account" : "Delete admin"}
                            disabled={admins.length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Admin Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit My Account" : "Create New Admin"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              error={!!formErrors.username}
              helperText={formErrors.username}
              fullWidth
              required
            />
            <PasswordTextField
              label="Password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              error={!!formErrors.password}
              helperText={
                formErrors.password ||
                (editMode ? "Leave blank to keep current password" : "")
              }
              fullWidth
              required={!editMode}
            />

            {/* Only super admins can set permissions for other admins */}
            {user?.isSuperAdmin && selectedAdmin && user.id !== selectedAdmin.id && (
              <>
                <Divider sx={{ my: 2 }} />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSuperAdmin}
                      onChange={(e) =>
                        setFormData({ ...formData, isSuperAdmin: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SuperAdminIcon />
                      <Typography>Super Admin</Typography>
                    </Box>
                  }
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Super admins can manage all admins and have access to all tabs.
                </Typography>

                {!formData.isSuperAdmin && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Allowed Tabs
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Select which tabs this admin can access
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <FormGroup>
                        <Grid container spacing={1}>
                          {ALL_MENU_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formData.permissions.includes(item)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFormData({
                                          ...formData,
                                          permissions: [...formData.permissions, item],
                                        });
                                      } else {
                                        setFormData({
                                          ...formData,
                                          permissions: formData.permissions.filter(
                                            (p) => p !== item
                                          ),
                                        });
                                      }
                                    }}
                                    size="small"
                                  />
                                }
                                label={<Typography variant="body2">{item}</Typography>}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormGroup>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => setFormData({ ...formData, permissions: [...ALL_MENU_ITEMS] })}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setFormData({ ...formData, permissions: [] })}
                      >
                        Clear All
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={24} />
            ) : editMode ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete My Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your account (
            <strong>{selectedAdmin?.username}</strong>)? This action cannot be
            undone and you will be logged out.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
